/*
 * Aura Velia shared interactions: scroll reveal, animated counters,
 * the luxury slider engine, accordions, wishlist/add-to-cart feedback,
 * brand film play toggle, button ripple.
 *
 * Re-entrant: init(root) only touches elements that haven't been
 * initialized yet, so it's safe to call again on Shopify theme-editor
 * section reloads (shopify:section:load).
 */
(function () {
  const sliderRegistry = new WeakMap();

  // A slider section can set independent layouts for desktop/mobile
  // (data-desktop-layout / data-mobile-layout = "slider" | "grid"). Grid mode
  // is pure CSS (see av-theme.css) — the drag/autoplay/infinite-loop engine
  // below must not run for whichever breakpoint is currently in grid mode.
  function effectiveSliderLayout(el) {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const layout = isDesktop ? el.dataset.desktopLayout : el.dataset.mobileLayout;
    return layout || 'slider';
  }

  function initReveal(root) {
    const els = [...root.querySelectorAll('.av-reveal:not([data-av-observed])')];
    // Content injected as a single node (infinite-scroll pagination, AJAX-hydrated
    // recommendations) can arrive with the .av-reveal class on `root` itself rather
    // than on a descendant, which querySelectorAll above would miss.
    if (root instanceof Element && root.matches('.av-reveal:not([data-av-observed])')) {
      els.push(root);
    }
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => {
      el.setAttribute('data-av-observed', '');
      observer.observe(el);
    });
  }

  function initCounters(root) {
    const els = root.querySelectorAll('[data-counter]:not([data-av-observed])');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.counter);
          const decimals = parseInt(el.dataset.decimal || '0', 10);
          const suffix = el.dataset.suffix || '';
          const duration = 1600;
          const startTime = performance.now();
          function tick(now) {
            const progress = Math.min(1, (now - startTime) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = (target * eased).toFixed(decimals) + suffix;
            if (progress < 1) requestAnimationFrame(tick);
          }
          requestAnimationFrame(tick);
          observer.unobserve(el);
        });
      },
      { threshold: 0.4 }
    );
    els.forEach((el) => {
      el.setAttribute('data-av-observed', '');
      observer.observe(el);
    });
  }

  class AvSlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.av-slider__viewport');
      this.track = root.querySelector('.av-slider__track');
      if (!this.viewport || !this.track) return;

      this.originalSlides = [...this.track.children];
      if (!this.originalSlides.length) return;

      this.dotsWrap = root.querySelector('.av-slider__dots');
      this.prevBtn = root.querySelector('[data-slider-prev]');
      this.nextBtn = root.querySelector('[data-slider-next]');
      this.loop = root.dataset.loop === 'true';
      // data-autoplay="0" (e.g. the banner slider's "Auto-advance slides" checkbox
      // turned off) explicitly disables autoplay — distinct from an unset/invalid
      // value, which falls back to the existing defaults below.
      const requestedAutoplay = parseInt(root.dataset.autoplay, 10);
      this.autoplayEnabled = requestedAutoplay !== 0;
      this.autoplayDelay = window.innerWidth < 750 ? 3000 : requestedAutoplay || 4000;
      this.dragDelta = 0;

      // Clone the full slide set on each side (not just one slide) so there's always
      // a full viewport of buffer content to slide into, however many items are visible
      // at once (1-up on mobile up to 6-up on desktop). One clone per side only works
      // when a single slide fills the viewport; with multiple visible slides it runs out
      // of buffer before the wrap point, which is what caused the visible "jump to first".
      this.cloneCount = this.loop ? this.originalSlides.length : 0;
      this.index = this.loop ? this.cloneCount : 0;

      if (this.loop) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.originalSlides.forEach((slide) => {
          before.appendChild(slide.cloneNode(true));
          after.appendChild(slide.cloneNode(true));
        });
        this.track.insertBefore(before, this.track.firstChild);
        this.track.appendChild(after);
      }

      this.slides = [...this.track.children];
      this.buildDots();
      this.bindEvents();
      // goTo() measures slideWidth() via getBoundingClientRect(), which forces
      // a synchronous layout — but calling it in the very same tick as the
      // clone insertion above means it's measuring a layout the browser has
      // only just started to settle (fonts/scrollbars/etc. can still shift
      // things a moment later). Deferring one frame lets that settle first,
      // so the initial position is correct instead of drifting until the
      // first next()/prev() call (which recomputes from a stable layout)
      // papers over it.
      requestAnimationFrame(() => {
        this.goTo(this.index, false);
        this.startAutoplay();
        requestAnimationFrame(() => this.root.classList.add('is-ready'));
      });

      this.onResize = () => {
        // The user resized/rotated across the 750px breakpoint into a state
        // where this axis is now configured as "grid" — tear down the slider
        // engine (and undo the loop-clone duplication) so CSS grid can render
        // the plain, un-cloned card list instead of fighting the JS transform.
        if (effectiveSliderLayout(this.root) === 'grid') {
          this.collapseToGrid();
          sliderRegistry.delete(this.root);
          return;
        }
        this.goTo(this.index, false);
      };
      window.addEventListener('resize', this.onResize);

      // goTo() caches slideWidth() into a fixed transform at whatever moment
      // it runs — usually right on load, before a late-arriving scrollbar
      // (content growing tall enough to need one) or a webfont swap has
      // finished changing the viewport's actual usable width. Neither of
      // those fires a window 'resize' event (the window itself didn't
      // change size, only the content inside it did), so the cached
      // transform silently drifts and a sliver of the adjacent slide peeks
      // in at the edge — persistently, not just during a transition.
      // ResizeObserver catches that class of change directly on the
      // viewport itself and re-centers immediately.
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, false));
        this.resizeObserver.observe(this.viewport);
      }
    }

    collapseToGrid() {
      this.destroy();
      this.track.innerHTML = '';
      this.originalSlides.forEach((slide) => this.track.appendChild(slide));
      this.track.style.transform = '';
      this.track.style.transition = '';
      this.viewport.classList.remove('is-dragging');
      this.root.classList.remove('is-ready');
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.originalSlides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'av-slider__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.onclick = () => this.goTo(this.loop ? i + this.cloneCount : i);
        this.dotsWrap.appendChild(b);
      });
    }

    // Toggles `.is-active-slide` on whichever slide is currently centered in
    // the viewport (accounting for loop clones sharing the same "real" index)
    // and updates the dots, if any. Generic/always-on rather than opt-in, so
    // any slider can style `.is-active-slide` — e.g. testimonials scaling the
    // center card larger than its neighbors — without the engine needing to
    // know which section is using it. Decoupled from dotsWrap's presence
    // (unlike the old dots-only version) since "Show Pagination" can be off
    // while the active-slide effect should still run.
    updateActiveSlide() {
      const total = this.originalSlides.length;
      const real = this.loop ? (((this.index - this.cloneCount) % total) + total) % total : this.index;
      this.slides.forEach((slide, i) => {
        const slideReal = this.loop ? (((i - this.cloneCount) % total) + total) % total : i;
        slide.classList.toggle('is-active-slide', slideReal === real);
      });
      if (this.dotsWrap) {
        [...this.dotsWrap.children].forEach((d, i) => d.classList.toggle('is-active', i === real));
      }
    }

    slideWidth() {
      const s = this.slides[0];
      const st = getComputedStyle(s);
      // offsetWidth (layout box), not getBoundingClientRect().width (painted/
      // visual box) — every slide carries `transform: scale(0.97)` until the
      // .is-ready reveal flips it to scale(1) (see the opacity/transform rule
      // on .av-slider__track > *), and getBoundingClientRect reports that
      // scaled-down size. Measuring before is-ready — which the initial
      // goTo() call always does — under-measured every slide by ~3%, and
      // that error multiplies by index when centering a looped slider, so
      // more slides meant a bigger, permanent-looking gap on load that only
      // "fixed itself" once a later, post-reveal goTo() (next/prev/autoplay)
      // measured correctly. offsetWidth ignores transforms entirely.
      return s.offsetWidth + parseFloat(st.marginRight || 0);
    }

    goTo(i, animate = true) {
      this.index = i;
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translate3d(${-this.slideWidth() * i}px,0,0)`;
      if (!animate) requestAnimationFrame(() => (this.track.style.transition = ''));
      this.updateActiveSlide();
    }

    // Rapid clicks interrupt the in-flight CSS transition before its transitionend
    // correction can run, so the index can drift arbitrarily far into (or past) the
    // cloned buffer with nothing to reel it back in. Recentering to the equivalent
    // real-slide position right before every step means the drift is corrected in
    // the same synchronous tick as the next animated move, so it's never painted.
    recenter() {
      if (!this.loop) return;
      const total = this.originalSlides.length;
      const real = (((this.index - this.cloneCount) % total) + total) % total;
      const canonical = this.cloneCount + real;
      if (canonical === this.index) return;
      this.index = canonical;
      this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(${-this.slideWidth() * this.index}px,0,0)`;
    }

    next() {
      this.recenter();
      this.goTo(this.index + 1);
      this.restartAutoplay();
    }

    prev() {
      this.recenter();
      this.goTo(this.index - 1);
      this.restartAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled) return;
      if (this.originalSlides.length < 2) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
      clearInterval(this.timer);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    bindEvents() {
      this.onTransitionEnd = () => {
        if (!this.loop) return;
        const total = this.originalSlides.length;
        // Once we've slid into the cloned buffer on either side, teleport back into the
        // real slide range. Uses modulo (not a single +/- total) so it fully resyncs in
        // one shot even if a burst of rapid clicks drifted the index by more than one
        // full set-length. Since the buffer is a full clone of every slide, the content
        // at the teleport target is pixel-identical to where we just were, so it's invisible.
        if (this.index < this.cloneCount || this.index >= this.cloneCount + total) {
          const real = (((this.index - this.cloneCount) % total) + total) % total;
          this.goTo(this.cloneCount + real, false);
        }
      };
      this.track.addEventListener('transitionend', this.onTransitionEnd);

      if (this.prevBtn) this.prevBtn.onclick = () => this.prev();
      if (this.nextBtn) this.nextBtn.onclick = () => this.next();

      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
      this.root.tabIndex = 0;
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      const controller = new AbortController();
      this.abortController = controller;
      const { signal } = controller;

      const start = (x) => {
        this.dragging = true;
        this.startX = x;
        this.viewport.classList.add('is-dragging');
        this.stopAutoplay();
      };
      const move = (x) => {
        if (this.dragging) this.dragDelta = x - this.startX;
      };
      const end = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.viewport.classList.remove('is-dragging');
        const threshold = this.slideWidth() * 0.15;
        if (this.dragDelta > threshold) this.prev();
        else if (this.dragDelta < -threshold) this.next();
        else this.goTo(this.index);
        this.dragDelta = 0;
        this.startAutoplay();
      };

      this.viewport.addEventListener(
        'mousedown',
        (e) => {
          e.preventDefault();
          start(e.clientX);
        },
        { signal }
      );
      window.addEventListener('mousemove', (e) => move(e.clientX), { signal });
      window.addEventListener('mouseup', end, { signal });
      this.viewport.addEventListener('touchstart', (e) => start(e.touches[0].clientX), { signal, passive: true });
      this.viewport.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { signal, passive: true });
      this.viewport.addEventListener('touchend', end, { signal });
    }

    destroy() {
      this.stopAutoplay();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.abortController) this.abortController.abort();
      if (this.onTransitionEnd) this.track.removeEventListener('transitionend', this.onTransitionEnd);
    }
  }

  function initSliders(root) {
    root.querySelectorAll('.av-slider:not([data-av-initialized])').forEach((el) => {
      el.setAttribute('data-av-initialized', '');
      if (effectiveSliderLayout(el) === 'grid') {
        // This breakpoint is in grid mode: leave the plain, un-cloned card
        // list alone for CSS grid to render, and only start watching for a
        // resize that flips this axis over to "slider" later.
        const onResize = () => {
          if (effectiveSliderLayout(el) !== 'slider' || sliderRegistry.has(el)) return;
          sliderRegistry.set(el, new AvSlider(el));
        };
        window.addEventListener('resize', onResize);
        return;
      }
      sliderRegistry.set(el, new AvSlider(el));
    });
  }

  function destroySliders(root) {
    root.querySelectorAll('.av-slider[data-av-initialized]').forEach((el) => {
      const instance = sliderRegistry.get(el);
      if (instance && instance.destroy) instance.destroy();
      sliderRegistry.delete(el);
    });
  }

  function initAccordions(root) {
    root.querySelectorAll('[data-av-accordion-group]:not([data-av-initialized])').forEach((group) => {
      group.setAttribute('data-av-initialized', '');
      group.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-av-accordion-trigger]');
        if (!trigger || !group.contains(trigger)) return;
        const item = trigger.closest('[data-av-accordion-item]');
        if (!item) return;
        const wasOpen = item.classList.contains('is-open');
        group.querySelectorAll('[data-av-accordion-item]').forEach((i) => i.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });
  }

  function initWishlist(root) {
    root.querySelectorAll('.av-product-card__wishlist:not([data-av-initialized])').forEach((btn) => {
      btn.setAttribute('data-av-initialized', '');
      btn.addEventListener('click', () => btn.classList.toggle('is-active'));
    });
  }

  let toastTimer;
  function showToast(message) {
    const toast = document.getElementById('avToast');
    const text = document.getElementById('avToastText');
    if (!toast || !text) return;
    text.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2200);
  }

  function initAddToCart(root) {
    root.querySelectorAll('.av-add-to-cart:not([data-av-initialized])').forEach((btn) => {
      btn.setAttribute('data-av-initialized', '');
      btn.addEventListener('click', () => {
        const card = btn.closest('.av-product-card');
        const name = (card && card.dataset.product) || 'Item';
        const span = btn.querySelector('span');
        const originalText = span ? span.textContent : '';
        btn.classList.add('is-added');
        if (span) span.textContent = 'Added';
        showToast(`${name} added to cart`);
        setTimeout(() => {
          btn.classList.remove('is-added');
          if (span) span.textContent = originalText;
        }, 1600);
      });
    });
  }

  function initFilm(root) {
    root.querySelectorAll('[data-av-film]:not([data-av-initialized])').forEach((film) => {
      film.setAttribute('data-av-initialized', '');
      const video = film.querySelector('video');
      const btn = film.querySelector('[data-av-film-toggle]');
      const playIcon = film.querySelector('[data-av-film-play-icon]');
      const pauseIcon = film.querySelector('[data-av-film-pause-icon]');
      if (!video || !btn) return;
      btn.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          film.classList.add('is-playing');
          if (playIcon) playIcon.style.display = 'none';
          if (pauseIcon) pauseIcon.style.display = 'block';
        } else {
          video.pause();
          film.classList.remove('is-playing');
          if (playIcon) playIcon.style.display = 'block';
          if (pauseIcon) pauseIcon.style.display = 'none';
        }
      });
    });
  }

  // Shared by testimonials AND the brand film section: a YouTube/Vimeo
  // iframe embed is only created on click (never on page load), so the
  // card/section stays a lightweight poster image until a visitor actually
  // chooses to play it — genuine lazy loading, not just a `loading="lazy"`
  // attribute on something that was already going to fetch immediately.
  // Shopify-hosted videos don't need this: they render as a native
  // <video preload="none"> tag, which the browser itself already defers
  // until play is pressed (or autoplay, where used, controls that itself).
  function initVideoEmbedTriggers(root) {
    root.querySelectorAll('.av-video-embed-trigger:not([data-av-initialized])').forEach((btn) => {
      btn.setAttribute('data-av-initialized', '');
      btn.addEventListener('click', () => {
        const src = btn.getAttribute('data-av-video-embed');
        if (!src) return;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.className = btn.getAttribute('data-av-video-class') || 'av-video-embed';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('loading', 'lazy');
        btn.replaceWith(iframe);
      });
    });
  }

  function initShareCopyLink(root) {
    root.querySelectorAll('[data-av-copy-link]:not([data-av-initialized])').forEach((btn) => {
      btn.setAttribute('data-av-initialized', '');
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-av-copy-link');
        try {
          await navigator.clipboard.writeText(url);
          showToast('Link copied');
        } catch {
          showToast('Could not copy link');
        }
      });
    });
  }

  function initInspiredByFilter(root) {
    root.querySelectorAll('[data-av-inspired-search]:not([data-av-initialized])').forEach((input) => {
      input.setAttribute('data-av-initialized', '');
      const section = input.closest('.av-section');
      const table = section && section.querySelector('[data-av-inspired-table]');
      const noResults = section && section.querySelector('[data-av-inspired-no-results]');
      if (!table) return;
      const rows = [...table.querySelectorAll('[data-av-inspired-row]')];
      input.addEventListener('input', () => {
        const query = input.value.trim().toLowerCase();
        let visibleCount = 0;
        rows.forEach((row) => {
          const matches = row.dataset.search.includes(query);
          row.hidden = !matches;
          if (matches) visibleCount += 1;
        });
        if (noResults) noResults.hidden = query === '' || visibleCount > 0;
      });
    });
  }

  function initConcentrationGuide(root) {
    root.querySelectorAll('.av-concentration-guide:not([data-av-initialized])').forEach((section) => {
      section.setAttribute('data-av-initialized', '');
      const items = section.querySelectorAll('.conc-item, .conc-item-withimg');
      if (!items.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('active');
          });
        },
        { threshold: 0.4 }
      );
      items.forEach((item) => observer.observe(item));
    });
  }

  // .product-information__media is a native Horizon static block with no
  // slot for custom markup inside it, so the badge renders in the section's
  // normal flow (blocks/_pdp-media-badge.liquid) and gets moved here instead
  // of editing that vendor block just to nest it.
  function initMediaBadge(root) {
    const badge = root.querySelector('[data-av-media-badge]:not([data-av-initialized])');
    if (!badge) return;
    badge.setAttribute('data-av-initialized', '');
    const media = document.querySelector('.product-information__media');
    if (!media) return;
    media.appendChild(badge);
    badge.classList.add('is-placed');
  }

  function initCountdown(root) {
    root.querySelectorAll('[data-av-countdown]:not([data-av-initialized])').forEach((el) => {
      el.setAttribute('data-av-initialized', '');
      const targetMs = parseInt(el.dataset.target, 10) * 1000;
      const daysEl = el.querySelector('[data-av-countdown-days]');
      const hoursEl = el.querySelector('[data-av-countdown-hours]');
      const minutesEl = el.querySelector('[data-av-countdown-minutes]');
      const secondsEl = el.querySelector('[data-av-countdown-seconds]');
      function pad(n) {
        return String(n).padStart(2, '0');
      }
      let timer;
      function tick() {
        const remaining = targetMs - Date.now();
        if (remaining <= 0) {
          el.hidden = true;
          clearInterval(timer);
          return;
        }
        const totalSeconds = Math.floor(remaining / 1000);
        daysEl.textContent = pad(Math.floor(totalSeconds / 86400));
        hoursEl.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
        minutesEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        secondsEl.textContent = pad(totalSeconds % 60);
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  }

  function initRipple(root) {
    root.querySelectorAll('.av-btn:not([data-av-ripple-bound]), .av-add-to-cart:not([data-av-ripple-bound])').forEach((btn) => {
      btn.setAttribute('data-av-ripple-bound', '');
      btn.classList.add('av-ripple');
      btn.addEventListener('click', function (e) {
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height) * 1.4;
        const dot = document.createElement('span');
        dot.className = 'av-ripple-dot';
        dot.style.width = dot.style.height = size + 'px';
        dot.style.left = e.clientX - rect.left + 'px';
        dot.style.top = e.clientY - rect.top + 'px';
        this.appendChild(dot);
        setTimeout(() => dot.remove(), 650);
      });
    });
  }

  function initPromoPopups(root) {
    if (root !== document) return;
    const template = document.querySelector('main')?.dataset.template || '';
    if (template === 'cart') return;

    document.querySelectorAll('[data-av-popup]:not([data-av-initialized])').forEach((popup) => {
      popup.setAttribute('data-av-initialized', '');

      const kind = popup.dataset.avPopup;
      const repeatHours = Number(popup.dataset.repeatHours || 24);
      const storageKey = `avPopupDismissedAt:${popup.id}`;
      const dismissedAt = Number(localStorage.getItem(storageKey) || 0);
      if (Date.now() - dismissedAt < repeatHours * 60 * 60 * 1000) return;

      const open = () => {
        if (document.querySelector('.av-promo-popup.is-visible')) return;
        popup.classList.add('is-visible');
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('av-no-scroll');
      };
      const close = () => {
        popup.classList.remove('is-visible');
        popup.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('av-no-scroll');
        localStorage.setItem(storageKey, String(Date.now()));
      };

      popup.querySelectorAll('[data-av-promo-close]').forEach((el) => el.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popup.classList.contains('is-visible')) close();
      });

      const copyBtn = popup.querySelector('[data-av-promo-copy]');
      if (copyBtn) {
        copyBtn.addEventListener('click', async () => {
          const code = copyBtn.getAttribute('data-av-promo-copy');
          try {
            await navigator.clipboard.writeText(code);
            showToast('Code copied');
          } catch {
            showToast('Could not copy code');
          }
        });
      }

      if (kind === 'entry') {
        const delay = Number(popup.dataset.delay || 2.5) * 1000;
        setTimeout(open, delay);
      } else if (kind === 'exit-intent') {
        const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
        if (isTouchDevice) return;
        const armDelay = Number(popup.dataset.armDelay || 4) * 1000;
        let armed = false;
        setTimeout(() => { armed = true; }, armDelay);
        const handleMouseOut = (e) => {
          if (!armed || e.clientY > 0 || e.relatedTarget) return;
          open();
          document.removeEventListener('mouseout', handleMouseOut);
        };
        document.addEventListener('mouseout', handleMouseOut);
      }
    });
  }

  function init(root) {
    initReveal(root);
    initCounters(root);
    initSliders(root);
    initAccordions(root);
    initWishlist(root);
    initAddToCart(root);
    initFilm(root);
    initVideoEmbedTriggers(root);
    initShareCopyLink(root);
    initConcentrationGuide(root);
    initInspiredByFilter(root);
    initMediaBadge(root);
    initCountdown(root);
    initRipple(root);
    initPromoPopups(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySliders(event.target));

  // Some content is swapped into the page without a `shopify:section:load` event:
  // infinite-scroll pagination (paginated-list.js does a raw grid.append/prepend)
  // and lazy-loaded product recommendations (fetched, then morphed in via AJAX
  // hydration). Neither reaches init() through the listener above, so newly
  // arrived .av-reveal cards would otherwise sit stuck at opacity: 0 forever.
  // Watch for any inserted node and initialize it the same way.
  //
  // Morphing (product-recommendations.js's morphSection) can also REUSE an
  // existing DOM node in place — e.g. a loading-skeleton item gets patched into
  // the real product card rather than being removed and re-inserted — which
  // only fires an `attributes` mutation on that node's `class`, never a
  // `childList` addedNodes entry for the node itself. Watch class changes too,
  // or a reused card's freshly-added .av-reveal class never gets observed.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target.nodeType === Node.ELEMENT_NODE) init(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) init(node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
})();
